import type { SupabaseClient } from '@supabase/supabase-js';

export type MessageTone = 'cold' | 'warm' | 'hot';

export interface SavedMessageCore {
    id: string;
    collection_id: string;
    user_id: string;
    message_content: string;
    tone: MessageTone;
    created_at: string;
    updated_at: string | null;
}

function tableMissing(err: any): boolean {
    const msg = String(err?.message || err?.details || err);
    return /relation .*collection_saved_messages.* does not exist/i.test(msg) || /table .*collection_saved_messages.* does not exist/i.test(msg);
}

export async function listSavedMessagesForCollectionCore(
    sb: SupabaseClient,
    collectionId: string,
    userId: string
): Promise<SavedMessageCore[]> {
    if (!collectionId) throw new Error('listSavedMessagesForCollectionCore: collectionId is required');
    if (!userId) throw new Error('listSavedMessagesForCollectionCore: userId is required');
    const { data, error } = await sb
        .from('collection_saved_messages')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
    if (error) {
        if (tableMissing(error)) {
            throw new Error('Collection saved messages feature not initialized. Add the collection_saved_messages table via migration and retry.');
        }
        throw error;
    }
    return (data || []) as SavedMessageCore[];
}

export async function createSavedMessageCore(
    sb: SupabaseClient,
    params: { collectionId: string; userId: string; messageContent: string; tone: MessageTone }
): Promise<SavedMessageCore> {
    const { collectionId, userId, messageContent, tone } = params;
    if (!collectionId) throw new Error('createSavedMessageCore: collectionId is required');
    if (!userId) throw new Error('createSavedMessageCore: userId is required');
    const { data, error } = await sb
        .from('collection_saved_messages')
        .insert({
            collection_id: collectionId,
            user_id: userId,
            message_content: messageContent,
            tone,
        })
        .select('*')
        .single();
    if (error) throw error;
    return data as SavedMessageCore;
}

export async function updateSavedMessageCore(
    sb: SupabaseClient,
    id: string,
    patch: { messageContent: string }
): Promise<SavedMessageCore> {
    if (!id) throw new Error('updateSavedMessageCore: id is required');
    const { data, error } = await sb
        .from('collection_saved_messages')
        .update({ message_content: patch.messageContent, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw error;
    return data as SavedMessageCore;
}

export async function deleteSavedMessageCore(sb: SupabaseClient, id: string): Promise<void> {
    if (!id) throw new Error('deleteSavedMessageCore: id is required');
    const { error } = await sb.from('collection_saved_messages').delete().eq('id', id);
    if (error) throw error;
}
